import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { itemListsApi } from "../../../api/item_lists";
import {
  aggregateProjectItemLists,
  formatNok,
} from "../../../components/dashboard/projects/tabs/projectItemListAggregates";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";

export const ProjectItemListsSummary = () => {
  const { t } = useTranslation();
  const { id: projectId } = useParams();
  const [itemLists, setItemLists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const response = await itemListsApi.getItemLists({ project_id: projectId });
        if (cancelled) return;
        if (response.data.success) {
          setItemLists(response.data.data || []);
        } else {
          setItemLists([]);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setItemLists([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (projectId) run();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const agg = aggregateProjectItemLists(itemLists);
  const backHref = `/dashboard/projects/${projectId}`;

  return (
    <div className="w-full h-full p-0">
      <DashboardHeader
        title={t("project.item_list.items_summary_page_title")}
        subtitle={t("project.item_list.items_summary_subtitle")}
        backUrl={backHref}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-6xl mx-auto">
        <p className="text-sm text-gray-600 mb-6 max-w-3xl">
          {t("project.item_list.items_summary_intro")}
        </p>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-white shadow rounded-lg p-4 border border-gray-100">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t("project.item_list.summary_total_quantity")}
                </div>
                <div className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">
                  {agg.totalQuantity}
                </div>
              </div>
              <div className="bg-white shadow rounded-lg p-4 border border-gray-100">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t("project.item_list.summary_total_purchase")}
                </div>
                <div className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">
                  {formatNok(agg.totalPurchase)}
                </div>
              </div>
              <div className="bg-white shadow rounded-lg p-4 border border-gray-100">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t("project.item_list.summary_total_sales")}
                </div>
                <div className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">
                  {formatNok(agg.totalSales)}
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("project.item_list.items_summary_table_item_number")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("project.item_list.product")}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("project.item_list.quantity")}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("project.item_list.summary_total_purchase")}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("project.item_list.summary_total_sales")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {agg.rowsByItemNumber.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-sm text-gray-500"
                        >
                          {t("project.item_list.no_items")}
                        </td>
                      </tr>
                    ) : (
                      agg.rowsByItemNumber.map((row) => (
                        <tr key={row.key}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {row.item_number != null && row.item_number !== ""
                              ? row.item_number
                              : "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {row.product_title}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 tabular-nums">
                            {row.quantity}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 tabular-nums">
                            {formatNok(row.total_purchase)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 tabular-nums">
                            {formatNok(row.total_sales)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
